/**
 * Custom TipTap Image extension for partner images.
 *
 * Stores images as `![alt](https://origin/base/#/partner/:partnerId/image/:imageId/view)` in markdown.
 * At render time, resolves partner image URLs to presigned download URLs
 * via PartnerImageService.
 *
 * Legacy format `![alt](partner-image:{imageId})` is still supported on parse
 * for backward compatibility with existing stored markdown.
 */

import Image from "@tiptap/extension-image";
import { VueNodeViewRenderer } from "@tiptap/vue-2";
import { PartnerModule } from "@/store/modules/partner";
import PartnerImageNodeView from "./PartnerImageNodeView.vue";

/** @deprecated Legacy prefix — kept for backward-compatible parsing only. */
export const PARTNER_IMAGE_PREFIX = "partner-image:";

/**
 * Regex matching partner image URLs in hash-route format:
 *   …#/partner(s)/:partnerId/image/:imageId/view
 *   …#/partner(s)/:partnerId/image/:imageId
 */
const IMAGE_URL_RE = /#\/partners?\/[^/]+\/image\/([^/?#\s)]+?)(?:\/view)?(?:[?#].*)?$/;

/**
 * Build an absolute partner image URL for the current environment.
 * Example: https://test.portal.schadensmeldung.digital/#/partner/607c.../image/640c.../view
 *
 * Falls back to legacy partner-image:imageId when no partnerId is available.
 */
export function buildPartnerImageUrl(imageId: string, partnerId?: string): string {
  const resolvedPartnerId = partnerId || PartnerModule.partner?._id || PartnerModule.partner?.id || "";
  if (!resolvedPartnerId) return `${PARTNER_IMAGE_PREFIX}${imageId}`;

  const base = window.location.origin + window.location.pathname;

  return `${base}#/partner/${resolvedPartnerId}/image/${imageId}/view`;
}

/**
 * Extract an image ID from a src string.
 * Supports:
 *   - URL format:    …#/partner/:partnerId/image/:imageId/view
 *   - Legacy format: partner-image:imageId
 *   - Regular URL:   returns null (not a partner image)
 */
export function parsePartnerImageSrc(src: string): string | null {
  // Try URL format first
  const urlMatch = IMAGE_URL_RE.exec(src);
  if (urlMatch) return urlMatch[1];

  // Try legacy partner-image: prefix
  if (src.startsWith(PARTNER_IMAGE_PREFIX)) {
    return src.slice(PARTNER_IMAGE_PREFIX.length) || null;
  }

  return null;
}

export const PartnerImageExtension = Image.extend({
  name: "image",

  addNodeView() {
    return VueNodeViewRenderer(PartnerImageNodeView);
  },

  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          let src = node.attrs.src || "";
          const alt = node.attrs.alt || "";
          const title = node.attrs.title;

          // Normalize legacy partner-image: sources to absolute URLs on re-save
          const imageId = parsePartnerImageSrc(src);
          if (imageId) {
            const upgraded = buildPartnerImageUrl(imageId);
            if (upgraded !== src) src = upgraded;
          }

          if (title) {
            state.write(`![${alt}](${src} "${title}")`);
          } else {
            state.write(`![${alt}](${src})`);
          }
        },
        parse: {
          // No special DOM transform needed for images —
          // tiptap-markdown natively parses ![alt](src) into <img> tags
          // which the Image extension picks up via parseDOM.
        }
      }
    };
  }
});
